/**
 * HTTP请求客户端
 * @description 基于Axios的HTTP请求封装，包含Token注入、取消重复请求、错误处理
 * @since 2025-12-27 优化版本
 */

import type { AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import type { ApiResponse } from '@/types/api'
import axios from 'axios'

import { ElMessage } from 'element-plus'

// ========== ApiResponse 包装工具 ==========
// 将 httpClient 响应（拦截器已解包 data 层）重新包装为统一的 ApiResponse<T> 格式
// 供需要 { code, msg, data } 结构的上层模块使用（如 authApi、usersApi）

import { STORAGE_KEYS } from '@/constants'
import { forceClearAuthState } from '@/store/modules/userCenter/authActions'

// CSRF Token 管理
import { CSRF_HEADER_NAME, getCSRFToken, shouldIncludeCSRFToken } from './security/csrf'

// ========== 状态锁与消息防抖 ==========
let isRedirecting = false
let lastToastTime = 0
let lastToastMessage = ''
const TOAST_COOLDOWN_MS = 2000

// ========== Token Refresh 锁与队列 ==========
let isRefreshing = false
let failedQueue: Array<{ resolve: (token: string) => void, reject: (err: unknown) => void }> = []

function processQueue(error: Error | null, token: string | null = null) {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error)
    }
    else {
      prom.resolve(token as string)
    }
  })
  failedQueue = []
}

function showDebouncedError(message: string) {
  const now = Date.now()
  if (message === lastToastMessage && now - lastToastTime < TOAST_COOLDOWN_MS) {
    return // 短时间内忽略相同的错误提示
  }
  lastToastMessage = message
  lastToastTime = now
  ElMessage.error(message)
}

// ========== 类型定义 ==========

interface PendingRequest {
  controller: AbortController
  timestamp: number
}

interface BusinessResponse<T = unknown> {
  code: string
  msg: string
  data: T
}

// ========== 常量定义 ==========

/** 成功状态码 */
const SUCCESS_CODE = '00000'

/** 需要重新登录的错误码 */
const AUTH_ERROR_CODES = ['A0001', 'A0002', 'A0003', 'A0230', 'A0231']

/** HTTP状态码消息映射 */
const HTTP_STATUS_MESSAGES: Record<number, string> = {
  400: '请求参数错误',
  401: '登录已过期，请重新登录',
  403: '没有权限访问该资源',
  404: '请求的资源不存在',
  408: '请求超时',
  500: '服务器内部错误',
  502: '网关错误',
  503: '服务不可用',
  504: '网关超时',
}

// ========== 请求管理 ==========

/** 存储pending请求的Map */
const pendingMap = new Map<string, PendingRequest>()

/**
 * 生成请求唯一标识
 */
function generateRequestKey(config: AxiosRequestConfig): string {
  const { method, url, params, data } = config
  let dataStr = ''
  if (data instanceof FormData) {
    if (data.has('chunkIndex')) {
      dataStr = `chunk_${data.get('chunkIndex')}`
    }
    else {
      dataStr = `formData_${Date.now()}_${Math.random()}`
    }
  }
  else {
    try {
      dataStr = JSON.stringify(data)
    }
    catch {
      dataStr = ''
    }
  }
  return [method, url, JSON.stringify(params), dataStr].join('&')
}

/**
 * 添加pending请求
 */
function addPendingRequest(config: InternalAxiosRequestConfig): void {
  const requestKey = generateRequestKey(config)

  // 如果已存在相同请求，先取消
  removePendingRequest(config)

  const controller = new AbortController()
  config.signal = controller.signal

  pendingMap.set(requestKey, {
    controller,
    timestamp: Date.now(),
  })
}

/**
 * 移除pending请求
 */
function removePendingRequest(config: AxiosRequestConfig): void {
  const requestKey = generateRequestKey(config)
  const pending = pendingMap.get(requestKey)

  if (pending) {
    pending.controller.abort()
    pendingMap.delete(requestKey)
  }
}

/**
 * 清除所有pending请求
 */
export function clearPendingRequests(): void {
  pendingMap.forEach((pending) => {
    pending.controller.abort()
  })
  pendingMap.clear()
}

// ========== Token管理 ==========

/**
 * 获取认证Token
 * @description 从localStorage获取token，支持多种存储key
 */
function getAuthToken(): string | null {
  // 检查localStorage是否可用（SSR/测试环境可能不存在）
  if (typeof localStorage === 'undefined') {
    return null
  }

  const isAdminRoute = window.location.pathname.startsWith('/admin')

  // 1. 如果在 Admin 路由，强制只读 admin token（避免混用前台 token）
  if (isAdminRoute) {
    return localStorage.getItem(STORAGE_KEYS.ADMIN_TOKEN)
  }

  // 2. 前台部分：Pinia 持久化的 user store（mech3d-user-v2 JSON 对象）
  try {
    const piniaData = localStorage.getItem(STORAGE_KEYS.USER_STORE_V2)
    if (piniaData) {
      const parsed = JSON.parse(piniaData) as Record<string, unknown>
      if (typeof parsed?.token === 'string' && parsed.token)
        return parsed.token
    }
  }
  catch {
    // JSON 解析失败，继续尝试后续 key
  }

  // 3. 兼容旧版独立 key
  const userToken = localStorage.getItem(STORAGE_KEYS.USER_TOKEN)
  if (userToken)
    return userToken

  const legacyToken = localStorage.getItem(STORAGE_KEYS.LEGACY_TOKEN)
  return legacyToken
}

// ========== 错误处理 ==========

/**
 * 处理HTTP错误
 */
function handleHttpError(status: number, url?: string, responseData?: unknown): void {
  let message = HTTP_STATUS_MESSAGES[status] || `请求失败 (${status})`
  const isAdminRoute = window.location.pathname.startsWith('/admin')

  // 优先使用后端返回的具体错误信息 (兼容 FastAPI / 自定义响应格式)
  if (responseData && typeof responseData === 'object') {
    const data = responseData as Record<string, unknown>
    if (typeof data.msg === 'string' && data.msg) {
      message = data.msg
    }
    else if (typeof data.detail === 'string' && data.detail) {
      message = data.detail
    }
  }

  // 需要静默处理的非关键接口白名单 (失败不弹提示，例如后台轮询/配置获取)
  const SILENT_ENDPOINTS = [
    '/users/me/dashboard',
    '/users/me/config',
    '/admin/dashboard/metrics',
    '/admin/dashboard/trend',
    '/admin/dashboard/distribution',
  ]
  const isSilentEndpoint = url && SILENT_ENDPOINTS.some(ep => url.includes(ep))

  if (isSilentEndpoint) {
    if (status === 401 || status === 403 || status === 404) {
      // 静默路由的错误让其自己消化
      return
    }
  }

  // 401需要特殊处理 - 跳转登录
  if (status === 401) {
    if (isRedirecting)
      return
    isRedirecting = true

    showDebouncedError(message)

    // 清除所有认证相关存储
    forceClearAuthState()

    // 延迟跳转，让用户看到提示
    setTimeout(() => {
      const loginPath = isAdminRoute ? '/admin/login' : '/auth/login'
      // 认证失效需硬刷新以清除全部前端状态（Pinia Store + localStorage + 内存缓存）
      window.location.href = `${loginPath}?redirect=${encodeURIComponent(window.location.pathname)}`
      isRedirecting = false
    }, 1500)
    return
  }

  // 403 权限验证处理
  if (status === 403) {
    const data = responseData as Record<string, unknown> | undefined
    // 🔒 用户被封禁处理
    if (data && typeof data === 'object' && data.code === 'USER_BANNED') {
      if (isRedirecting)
        return
      isRedirecting = true

      showDebouncedError('您的账号已被禁用，如有疑问请联系客服')

      forceClearAuthState()
      setTimeout(() => {
        window.location.href = '/auth/login?reason=banned'
        isRedirecting = false
      }, 1500)
      return
    }

    // 🛠️ Admin专属权限不足处理
    if (isAdminRoute) {
      if (isRedirecting)
        return
      isRedirecting = true

      showDebouncedError('权限不足，请联系管理员')

      setTimeout(() => {
        window.location.href = '/admin/403'
        isRedirecting = false
      }, 1500)
      return
    }
  }

  // 其他错误显示防抖通知
  showDebouncedError(message)
  console.error(`[httpClient] HTTP Error ${status}:`, url)
}

/**
 * 处理业务错误
 */
function handleBusinessError(code: string, msg: string): void {
  // 认证相关错误
  if (AUTH_ERROR_CODES.includes(code)) {
    if (isRedirecting)
      return
    isRedirecting = true

    showDebouncedError(msg || '登录已过期，请重新登录')

    // 清除所有认证相关存储
    forceClearAuthState()

    setTimeout(() => {
      const isAdminRoute = window.location.pathname.startsWith('/admin')
      const loginPath = isAdminRoute ? '/admin/login' : '/auth/login'
      // 业务层认证错误码触发，需硬刷新以清除全部前端状态
      window.location.href = `${loginPath}?redirect=${encodeURIComponent(window.location.pathname)}`
      isRedirecting = false
    }, 1500)
    return
  }

  // 通用业务错误
  showDebouncedError(msg || '操作失败，请稍后重试')
}

// ========== Axios实例 ==========

const service: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_PREFIX || '/api',
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json;charset=utf-8',
  },
})

// 请求拦截器
service.interceptors.request.use(
  (config) => {
    // 添加到pending请求
    addPendingRequest(config)

    // Token注入 (如果未被重试逻辑提前注入)
    if (!config.headers.Authorization) {
      const token = getAuthToken()
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
    }

    // 如果是 FormData，删除默认的 application/json，让浏览器自动生成带 boundary 的 multipart/form-data
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type']
    }

    // CSRF Token 注入（敏感操作自动添加）
    if (shouldIncludeCSRFToken(config.method)) {
      const csrfToken = getCSRFToken()
      if (csrfToken) {
        config.headers[CSRF_HEADER_NAME] = csrfToken
      }
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  },
)

// 响应拦截器
service.interceptors.response.use(
  (response: AxiosResponse<BusinessResponse>) => {
    // 从pending中移除
    removePendingRequest(response.config)

    const res = response.data

    // 兼容两种响应格式：
    // 格式1（标准包装）: { code: '00000', msg: 'success', data: T }
    // 格式2（直接返回）: T（后端未包装）
    //
    // 通过检测是否存在 'code' 字段来区分格式
    if (res && typeof res === 'object' && 'code' in res) {
      // 标准包装格式：处理业务错误码
      if (res.code !== SUCCESS_CODE) {
        handleBusinessError(res.code, res.msg)
        return Promise.reject(new Error(res.msg || 'Error'))
      }
      // 解包并返回 data 层
      return res.data as unknown as AxiosResponse<BusinessResponse>
    }

    // 直接返回格式：后端未使用标准包装，原样返回
    // 设计说明：Axios 拦截器期望返回 AxiosResponse，但我们直接返回 data 以简化调用方。
    // 调用方通过 httpClient.get<T>() 等泛型方法获得正确类型。
    // 此处类型断言是 Axios 拦截器模式的标准做法，调用方类型安全由泛型保证。
    return res as unknown as AxiosResponse<BusinessResponse>
  },
  (error) => {
    // 从pending中移除
    if (error.config) {
      removePendingRequest(error.config)
    }

    // 取消请求不显示错误
    if (axios.isCancel(error)) {
      return Promise.reject(error)
    }

    const { config, response } = error

    // 401 Token 无感续签逻辑
    if (response && response.status === 401 && !config.url?.includes('/auth/refresh-token')) {
      if (!isRefreshing) {
        isRefreshing = true

        let refreshToken = ''
        try {
          // 从持久化存储尝试获取 refreshToken (兼容 V2 和 V1)
          const v2Str = localStorage.getItem(STORAGE_KEYS.USER_STORE_V2)
          if (v2Str)
            refreshToken = JSON.parse(v2Str).refreshToken || ''

          if (!refreshToken) {
            const v1Str = localStorage.getItem(STORAGE_KEYS.USER_STORE_V1)
            if (v1Str)
              refreshToken = JSON.parse(v1Str).refreshToken || ''
          }
        }
        catch {
          // 解析失败忽略
        }

        if (!refreshToken) {
          isRefreshing = false
          handleHttpError(response.status, config.url, response.data)
          return Promise.reject(error)
        }

        return new Promise((resolve, reject) => {
          // 记录刷新前的 AccessToken，用于并发/多Tab竞争检测
          const currentAccessToken = getAuthToken()

          // 绕过拦截器，使用裸 axios 发起刷新请求
          axios.post(`${import.meta.env.VITE_API_PREFIX || '/api'}/auth/refresh-token`, { refreshToken })
            .then((res) => {
              const resData = res.data
              if (resData && resData.code === '00000' && resData.data && (resData.data.accessToken || resData.data.token)) {
                const newToken = resData.data.accessToken || resData.data.token

                // 强制更新 localStorage，供后续 request 拦截器使用
                try {
                  const isAdminRoute = window.location.pathname.startsWith('/admin')

                  if (isAdminRoute) {
                    localStorage.setItem(STORAGE_KEYS.ADMIN_TOKEN, newToken)
                  }
                  else {
                    const storageStr = localStorage.getItem(STORAGE_KEYS.USER_STORE_V2)
                    if (storageStr) {
                      const storeObj = JSON.parse(storageStr)
                      storeObj.token = newToken
                      if (resData.data.refreshToken) {
                        storeObj.refreshToken = resData.data.refreshToken
                      }
                      localStorage.setItem(STORAGE_KEYS.USER_STORE_V2, JSON.stringify(storeObj))
                    }

                    // 同步更新 Pinia Store 内存状态（动态引入以避免循环依赖）
                    import('@/store').then(({ useUserStore, default: pinia }) => {
                      const userStore = useUserStore(pinia)
                      userStore.setToken(newToken)
                      if (resData.data.refreshToken) {
                        userStore.setRefreshToken(resData.data.refreshToken)
                      }
                    }).catch(e => console.error('[TokenSync] Failed to sync pinia store:', e))
                  }
                }
                catch {}

                // 重放当前请求
                isRefreshing = false
                config.headers.Authorization = `Bearer ${newToken}`
                processQueue(null, newToken)
                resolve(service(config))
              }
              else {
                // 延迟检查以解决多Tab竞态条件（等待其他Tab将新Token写入持久化存储）
                setTimeout(() => {
                  isRefreshing = false // 宽限期结束后才释放锁
                  const newlyReadToken = getAuthToken()
                  if (newlyReadToken && newlyReadToken !== currentAccessToken) {
                    config.headers.Authorization = `Bearer ${newlyReadToken}`
                    processQueue(null, newlyReadToken)
                    resolve(service(config))
                  }
                  else {
                    // 刷新失败，强制登出并将队列中的请求全部 reject
                    processQueue(new Error('Refresh failed'))
                    reject(error)
                    // 强制触发登出逻辑（使用非静默 URL 绕过 isSilentEndpoint 检查）
                    handleHttpError(401, '/auth/refresh-token-failed', resData)
                  }
                }, 500)
              }
            })
            .catch((err) => {
              // 延迟检查以解决多Tab竞态条件
              setTimeout(() => {
                isRefreshing = false // 宽限期结束后才释放锁
                const newlyReadToken = getAuthToken()
                if (newlyReadToken && newlyReadToken !== currentAccessToken) {
                  config.headers.Authorization = `Bearer ${newlyReadToken}`
                  processQueue(null, newlyReadToken)
                  resolve(service(config))
                }
                else {
                  processQueue(err)
                  reject(error)
                  // 强制触发登出逻辑（使用非静默 URL 绕过 isSilentEndpoint 检查）
                  handleHttpError(401, '/auth/refresh-token-failed', err.response?.data || response.data)
                }
              }, 500)
            })
            .finally(() => {
              // isRefreshing 重置已分别在成功路径（resolve 前）和失败路径（setTimeout 内）处理
              // 此处无需再次设置
            })
        })
      }
      else {
        // 正在刷新中，将后续请求挂起加入队列
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: (token: string) => {
              config.headers.Authorization = `Bearer ${token}`
              resolve(service(config))
            },
            reject: (err: unknown) => {
              reject(err)
            },
          })
        })
      }
    }

    // 处理正常的HTTP错误
    if (response) {
      handleHttpError(response.status, config?.url, response.data)
    }
    else if (error.message?.includes('timeout')) {
      showDebouncedError('请求超时，请检查网络连接')
    }
    else if (error.message?.includes('Network Error')) {
      showDebouncedError('网络连接失败，请检查网络')
    }
    else {
      showDebouncedError('请求失败，请稍后重试')
    }

    return Promise.reject(error)
  },
)

// ========== 导出接口 ==========

export const httpClient = {
  get<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return service.get(url, config)
  },

  post<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    return service.post(url, data, config)
  },

  put<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    return service.put(url, data, config)
  },

  patch<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    return service.patch(url, data, config)
  },

  delete<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return service.delete(url, config)
  },

  /**
   * 上传文件
   */
  upload<T = unknown>(url: string, formData: FormData, config?: AxiosRequestConfig): Promise<T> {
    return service.post(url, formData, {
      ...config,
      headers: {
        ...config?.headers,
      },
    })
  },
}

export default httpClient

// =========================================================================
// API 包装器 (API Wrappers) - 架构决策说明 [Sprint P1_03_Patch]
// -------------------------------------------------------------------------
// 价值定位：此处的 wrapGet/wrapPost 等方法作为**向后兼容的适配器层**存在。
// 背景：底层的 httpClient 拦截器已将标准响应 {code, msg, data} 解包，但在迁移
//   过程中，大量旧的业务代码 (如 auth、userCenter 组件) 深度依赖了 ApiResponse
//   结构（仍使用 if (res.code === '00000') 并读取 res.data）。
// 规范：
// 1. 旧有或强依赖旧接口的 API 模块（如 auth.ts, users.ts）继续使用 wrapX 包装，
//    避免对全量前端组件进行破坏性重构；
// 2. 对于全新开发的 API 模块（如 notification.ts），应直接使用 httpClient 并
//    返回数据实体。
// =========================================================================

export async function wrapGet<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
  const resp = await httpClient.get<T>(url, config)
  return { code: '00000', msg: 'success', data: resp as T }
}

export async function wrapPost<T, D = unknown>(url: string, data?: D, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
  const resp = await httpClient.post<T>(url, data, config)
  return { code: '00000', msg: 'success', data: resp as T }
}

export async function wrapPut<T, D = unknown>(url: string, data?: D, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
  const resp = await httpClient.put<T>(url, data, config)
  return { code: '00000', msg: 'success', data: resp as T }
}

export async function wrapPatch<T, D = unknown>(url: string, data?: D, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
  const resp = await httpClient.patch<T>(url, data, config)
  return { code: '00000', msg: 'success', data: resp as T }
}

export async function wrapDelete<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
  const resp = await httpClient.delete<T>(url, config)
  return { code: '00000', msg: 'success', data: resp as T }
}
